<main class="file-compliance" aria-labelledby="file-compliance-heading">
  <h1 id="file-compliance-heading" class="visually-hidden">Selected file compliance summary</h1>
  <p class="visually-hidden" aria-live="polite" aria-atomic="true">{{ announcement }}</p>

  @if (loading) {
    <mat-progress-bar mode="indeterminate" aria-label="Loading file compliance summary"></mat-progress-bar>
  }

  @if (error) {
    <div class="state error-state" role="alert">
      <mat-icon>error_outline</mat-icon>
      <span>{{ error }}</span>
      <button mat-button type="button" (click)="retry()">Retry</button>
    </div>
  }

  @if (rebuilding) {
    <div class="state preparing-state" role="status">
      <mat-icon>hourglass_top</mat-icon>
      <span>Results are still being prepared. No historical result is being presented as current.</span>
    </div>
  }

  @if (stale) {
    <div class="state stale-state" role="status">
      <mat-icon>history</mat-icon>
      <span>Some source pages changed. Current risk counts are withheld while this summary rebuilds.</span>
    </div>
  }

  @if (allEvaluatedLow) {
    <div class="state precise-state" role="status">
      <mat-icon>info</mat-icon>
      <span>All evaluated pages are Low Risk for the rows shown. This is not a file-wide compliance grade.</span>
    </div>
  }

  @if (noResults) {
    <section class="empty-state">
      <mat-icon>rule_folder</mat-icon>
      <h2>No compliance results yet</h2>
      <p>No page in this file has a persisted compliance result. Run page analysis first or review active tasks.</p>
      <button mat-stroked-button type="button" [routerLink]="['/projects', projectId, 'tasks']">View tasks</button>
    </section>
  }

  @if ((summary?.rowsList?.length ?? 0) > 0) {
    <div #tableShell class="table-shell">
      <table class="summary-table">
        <thead>
          <tr>
            <th scope="col">
              <div class="header-content">
                <span>Section</span>
                <app-column-filter
                  [config]="sectionFilterConfig"
                  [criterion]="sectionCriterion"
                  (criterionChange)="onSectionCriterionChange($event)"
                ></app-column-filter>
              </div>
            </th>
            <th scope="col">
              <div class="header-content">
                <span>Code relevance</span>
                <app-column-filter
                  [config]="relevanceFilterConfig"
                  [criterion]="relevanceCriterion"
                  (criterionChange)="onRelevanceCriterionChange($event)"
                ></app-column-filter>
              </div>
            </th>
            <th scope="col">
              <div class="header-content">
                <span>Compliance</span>
                <app-column-filter
                  [config]="resultFilterConfig"
                  [criterion]="resultCriterion"
                  (criterionChange)="onResultCriterionChange($event)"
                ></app-column-filter>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          @for (group of bookGroups; track group.bookId) {
            @for (row of group.rows; track row.canonicalSectionId) {
              <tr class="section-row" [class.rollup-row]="row.isRollup" [class.book-section-row]="row.depth === 0">
                <th scope="row" [style.--section-depth]="row.depth">
                  <div class="section-heading">
                    @if (row.hasChildren) {
                      <button
                        type="button"
                        class="section-expander"
                        [attr.aria-label]="(isExpanded(row) ? 'Collapse ' : 'Expand ') + row.title"
                        [attr.aria-expanded]="isExpanded(row)"
                        (click)="toggleRow(row)"
                      >
                        <mat-icon>{{ isExpanded(row) ? 'expand_more' : 'chevron_right' }}</mat-icon>
                      </button>
                    } @else {
                      <span class="section-expander-placeholder" aria-hidden="true"></span>
                    }
                    <span class="section-icon" aria-hidden="true">
                      <mat-icon>shield</mat-icon>
                    </span>
                    <span class="section-text">
                      <span class="section-title">{{ row.title || row.canonicalSectionId }}</span>
                      @if (sectionContext(row)) {
                        <span class="section-context">{{ sectionContext(row) }}</span>
                      }
                    </span>
                  </div>
                </th>
                <td class="relevance-cell">
                  @if (row.fileRelevanceSummary) {
                    <button
                      type="button"
                      class="relevance-chip"
                      [ngClass]="relevanceClass(row.fileRelevance)"
                      [matTooltip]="row.fileRelevanceSummary"
                      [attr.aria-expanded]="isRationaleExpanded(row)"
                      [attr.aria-controls]="rationaleId(row)"
                      (click)="toggleRationale(row)"
                    >
                      {{ relevanceLabel(row.fileRelevance) }}
                    </button>
                    <p class="relevance-details" [class.expanded]="isRationaleExpanded(row)" [id]="rationaleId(row)">
                      {{ row.fileRelevanceSummary }}
                      @if (isRationaleExpanded(row) && row.relevanceSourcePagesList.length > 0) {
                        <span> Contributing pages: {{ row.relevanceSourcePagesList.join(', ') }}. </span>
                      }
                    </p>
                  } @else {
                    <span class="relevance-chip" [ngClass]="relevanceClass(row.fileRelevance)">
                      {{ relevanceLabel(row.fileRelevance) }}
                    </span>
                  }
                </td>
                <td class="compliance-cell">
                  <div class="risk-chips">
                    @for (filter of riskFilters; track filter) {
                      @if (chipCount(row, filter) > 0) {
                        <button
                          type="button"
                          class="app-compliance-count-chip compliance-chip risk-chip"
                          [id]="chipId(row, filter)"
                          [class.app-compliance-count-high]="filter === 'high'"
                          [class.app-compliance-count-medium]="filter === 'medium'"
                          [class.app-compliance-count-low]="filter === 'low'"
                          [class.risk-high]="filter === 'high'"
                          [class.risk-medium]="filter === 'medium'"
                          [class.risk-low]="filter === 'low'"
                          [class.selected]="isSelected(row, filter)"
                          [attr.aria-label]="chipAriaLabel(row, filter)"
                          [attr.aria-expanded]="isSelected(row, filter)"
                          [attr.aria-controls]="isSelected(row, filter) ? drilldownId(row) : null"
                          [matTooltip]="chipAriaLabel(row, filter)"
                          (click)="activateFilter(row, filter)"
                        >
                          <strong>{{ chipCount(row, filter) }}</strong>
                        </button>
                      }
                    }
                    @for (filter of coverageFilters; track filter) {
                      @if (chipCount(row, filter) > 0) {
                        <button
                          type="button"
                          class="app-compliance-count-chip coverage-chip"
                          [class.app-compliance-count-missing]="filter === 'missing'"
                          [class.app-compliance-count-stale]="filter === 'stale'"
                          [class.coverage-missing]="filter === 'missing'"
                          [class.coverage-stale]="filter === 'stale'"
                          [id]="chipId(row, filter)"
                          [class.selected]="isSelected(row, filter)"
                          [attr.aria-label]="chipAriaLabel(row, filter)"
                          [attr.aria-expanded]="isSelected(row, filter)"
                          [attr.aria-controls]="isSelected(row, filter) ? drilldownId(row) : null"
                          [matTooltip]="chipAriaLabel(row, filter)"
                          (click)="activateFilter(row, filter)"
                        >
                          <strong>{{ chipCount(row, filter) }}</strong>
                        </button>
                      }
                    }
                    <span class="compliance-context">{{ complianceContext(row) }}</span>
                  </div>
                </td>
              </tr>
              @if (
                selectedRow?.iccBookId === row.iccBookId && selectedRow?.canonicalSectionId === row.canonicalSectionId
              ) {
                <tr class="drilldown-row">
                  <td colspan="3">
                    <section
                      class="drilldown"
                      tabindex="-1"
                      [id]="drilldownId(row)"
                      [attr.aria-labelledby]="drilldownId(row) + '-heading'"
                    >
                      <div class="drilldown-heading">
                        <div>
                          <h2 [id]="drilldownId(row) + '-heading'">
                            {{ selectedFilterLabel() }} pages for {{ row.canonicalSectionId }}
                          </h2>
                          <p>
                            {{ totalHitCount }} matching {{ totalHitCount === 1 ? 'page' : 'pages' }};
                            {{ coverageText }}
                          </p>
                        </div>
                        <button
                          mat-icon-button
                          type="button"
                          aria-label="Close page results"
                          (click)="clearDrilldown()"
                        >
                          <mat-icon>close</mat-icon>
                        </button>
                      </div>
                      @if (drilldownLoading && pageHits.length === 0) {
                        <mat-progress-bar mode="indeterminate" aria-label="Loading matching pages"></mat-progress-bar>
                      }
                      @if (drilldownError) {
                        <p class="drilldown-error" role="alert">{{ drilldownError }}</p>
                      }
                      <div class="page-hits">
                        @for (hit of pageHits; track hit.pageNumber) {
                          <button
                            type="button"
                            class="page-hit"
                            [attr.aria-label]="
                              'Open ' + pageHitIdentity(hit) + ', ' + (hit.pageTitle || 'compliance section')
                            "
                            (click)="openPage(hit)"
                          >
                            <mat-icon>description</mat-icon>
                            <span>
                              <strong>{{ pageHitIdentity(hit) }}</strong>
                              <small>{{ hit.pageTitle || 'Open compliance section' }}</small>
                              <small>{{ pageHitMetadata(hit) }}</small>
                            </span>
                            <mat-icon>chevron_right</mat-icon>
                          </button>
                        }
                      </div>
                      @if (nextPageToken) {
                        <button mat-stroked-button type="button" (click)="loadMore()" [disabled]="drilldownLoading">
                          Load more
                        </button>
                      }
                    </section>
                  </td>
                </tr>
              }
            }
          }
        </tbody>
      </table>
    </div>
    @if (filtersActive && bookGroups.length === 0) {
      <p class="filtered-empty" role="status">No code sections match the active search and filters.</p>
    }
  }
</main>
